import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { validateEmail } from '../../util';
import { validatePasswordStrength, isPasswordStrong, PASSWORD_RULES } from '../../utils/functions';
import { useAppContext } from '../../context/AppContext';
import { useNotification } from '../../components/notifications/useNotification';
import FormButton from '../../components/FormButton/FormButton';
import FormField from '../../components/FormField/FormField';
import TermsModal from '../../components/Modals/TermsModal/TermsModal';
import PublicNavbar from '../../components/PublicNavbar/PublicNavbar';
import axios from 'axios';
import useDocumentMeta from '../../utils/useDocumentMeta';
import './RegisterPage.css';

const RegisterPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setDietist, currentLanguage } = useAppContext();
    const notification = useNotification();
    useDocumentMeta('seo-register-title', 'seo-register-desc', { canonicalPath: '/register' });

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        addresses: [''],
        referralCode: searchParams.get('invite') || '',
        acceptedTerms: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const formDataRef = useRef(formData);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        if (!window.google) return;
        const container = document.getElementById('nrp-google-container');
        if (!container) return;
        //container.innerHTML = '';
        window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleRegister,
        });
        window.google.accounts.id.renderButton(
            container,
            { theme: 'outline', size: 'large', width: 200, locale: currentLanguage }
        );
    }, [currentLanguage]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleAddressChange = (index, value) => {
        setFormData(prev => {
            const updated = [...prev.addresses];
            updated[index] = value;
            return { ...prev, addresses: updated };
        });
    };

    const addAddress = () => {
        if (formData.addresses.length >= 5) return;
        setFormData(prev => ({ ...prev, addresses: [...prev.addresses, ''] }));
    };

    const removeAddress = (index) => {
        setFormData(prev => ({
            ...prev,
            addresses: prev.addresses.filter((_, i) => i !== index),
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.fullName.trim())             newErrors.fullName = t('nrp-fullname-required');
        if (!formData.email)                      newErrors.email = t('errorMessages.emailRequired');
        else if (!validateEmail(formData.email))  newErrors.email = t('errorMessages.emailInvalid');
        if (!formData.password)                   newErrors.password = t('errorMessages.password-required');
        else if (!isPasswordStrong(formData.password)) newErrors.password = t('errorMessages.password-too-weak');
        if (!formData.referralCode.trim())          newErrors.referralCode = t('errorMessages.referral-code-required');
        if (!formData.acceptedTerms)              newErrors.acceptedTerms = t('errorMessages.accept-terms-and-conditions');
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            Object.values(newErrors).forEach(msg => notification.showWarning(msg));
            return false;
        }
        return true;
    };

    const handleGoogleRegister = async (response) => {
        const current = formDataRef.current;
        if (!current.acceptedTerms) {
            setErrors(prev => ({ ...prev, acceptedTerms: t('errorMessages.accept-terms-and-conditions') }));
            notification.showWarning(t('errorMessages.accept-terms-and-conditions'));
            return;
        }
        if (!current.referralCode.trim()) {
            setErrors(prev => ({ ...prev, referralCode: t('errorMessages.referral-code-required') }));
            notification.showWarning(t('errorMessages.referral-code-required'));
            return;
        }
        try {
            setLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/dietist/login/google`,
                {
                    token: response.credential,
                    referralCode: current.referralCode,
                },
                { withCredentials: true }
            );
            setDietist(res.data.dietist);
            navigate('/home');
        } catch (error) {
            console.error('Google register failed:', error);
            notification.showError(t('errorMessages.unknown-error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            setLoading(true);
            const payload = {
                ...formData,
                addresses: formData.addresses.filter(a => a.trim()),
                language: currentLanguage,
            };
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/dietist/register`, payload);
            navigate('/check-email', { state: { email: formData.email } });
        } catch (error) {
            console.error('Registration error:', error);
            switch (error.response?.status) {
                case 400:
                    setErrors(prev => ({ ...prev, referralCode: t('errorMessages.invalid-referral-code') }));
                    notification.showError(t('errorMessages.invalid-referral-code'));
                    break;
                case 409:
                    setErrors(prev => ({ ...prev, email: t('email-already-registered') }));
                    notification.showError(t('email-already-registered'));
                    break;
                case 429:
                    notification.showWarning(t('errorMessages.too-many-requests'));
                    break;
                default:
                    notification.showError(t('errorMessages.unknown-error'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <PublicNavbar />
        <div className="nrp">
            <div className="nrp-card">

                {/* ── HERO IMAGE ── */}
                <div className="nrp-hero-wrapper">
                    <img
                        className="nrp-hero-image"
                        src="/images/RegisterPage/hero-image.png"
                        alt="Fresh healthy vegetables and professional nutrition tools"
                    />
                </div>

                {/* ── HEADER ── */}
                <div className="nrp-header">
                    <h1 className="nrp-title">{t('Create-your-account')}</h1>
                    <p className="nrp-subtitle">{t('nrp-subtitle')}</p>
                </div>

                {/* ── FORM ── */}
                <form className="nrp-form" onSubmit={handleSubmit} noValidate>

                    {/* Full Name */}
                    <FormField
                        label={t('nrp-full-name-label')}
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder={t('nrp-enter-full-name')}
                        icon="👤"
                        error={errors.fullName}
                        maxLength={100}
                        autoComplete="name"
                    />

                    {/* Email */}
                    <FormField
                        label={t('Email')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('enter-email')}
                        icon="✉"
                        error={errors.email}
                        maxLength={254}
                        autoComplete="email"
                    />

                    {/* Password */}
                    <div className="nrp-pwd-wrapper">
                        <FormField
                            label={t('nrp-password-label')}
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="••••••••"
                            icon="🔒"
                            error={errors.password}
                            maxLength={128}
                            autoComplete="new-password"
                        />
                        {formData.password.length > 0 && (() => {
                            const checks = validatePasswordStrength(formData.password);
                            const allOk = PASSWORD_RULES.every(({ key }) => checks[key]);
                            if (allOk) return null;
                            return (
                                <ul className={`nrp-pwd-checklist${errors.password ? ' nrp-pwd-checklist--error' : ''}`}>
                                    {PASSWORD_RULES.map(({ key, i18nKey }) => (
                                        <li
                                            key={key}
                                            className={checks[key] ? 'nrp-pwd-rule--ok' : (errors.password ? 'nrp-pwd-rule--error' : 'nrp-pwd-rule--fail')}
                                        >
                                            <span className="nrp-pwd-rule-icon">{checks[key] ? '✓' : '✗'}</span>
                                            {t(i18nKey)}
                                        </li>
                                    ))}
                                </ul>
                            );
                        })()}
                    </div>

                    {/* Referral / Invitation code */}
                    <FormField
                        label={t('referral-code')}
                        name="referralCode"
                        value={formData.referralCode}
                        onChange={handleInputChange}
                        placeholder={t('enter-referral-code')}
                        icon="🏷"
                        error={errors.referralCode}
                        maxLength={50}
                        required
                    />

                    {/* Addresses */}
                    <div className="nrp-field">
                        <label className="nrp-label">{t('nrp-address-label')}</label>
                        {formData.addresses.map((addr, i) => (
                            <div key={i} className="nrp-address-row">
                                <FormField
                                    name={`address-${i}`}
                                    value={addr}
                                    onChange={e => handleAddressChange(i, e.target.value)}
                                    placeholder={t('nrp-enter-address')}
                                    icon="📍"
                                    className="nrp-address-input"
                                    maxLength={200}
                                    autoComplete="street-address"
                                />
                                {formData.addresses.length > 1 && (
                                    <button
                                        type="button"
                                        className="nrp-remove-btn"
                                        onClick={() => removeAddress(i)}
                                        aria-label={t('nrp-remove-address')}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                        {formData.addresses.length < 5 && (
                            <button type="button" className="nrp-add-address-btn" onClick={addAddress}>
                                {t('nrp-add-address')}
                            </button>
                        )}
                    </div>
                    <FormButton
                        type="submit"
                        fullWidth
                        loading={loading}
                        loadingText={t('sending')}
                    >
                        {t('Register')}
                    </FormButton>

                    {/* OR divider */}
                    <div className="nrp-divider"><span>{t('or')}</span></div>

                    {/* Google Sign-In */}
                    <div className="nrp-google-wrapper">
                        <div id="nrp-google-container" />
                        {!formData.acceptedTerms && (
                            <div
                                className="nrp-google-overlay"
                                onClick={() => {
                                    setErrors(prev => ({ ...prev, acceptedTerms: t('errorMessages.accept-terms-and-conditions') }));
                                    notification.showWarning(t('errorMessages.accept-terms-and-conditions'));
                                }}
                            />
                        )}
                    </div>

                    {/* Terms checkbox */}
                    <div className="nrp-terms-row">
                        <label className="nrp-checkbox-label">
                            <input
                                type="checkbox"
                                name="acceptedTerms"
                                checked={formData.acceptedTerms}
                                onChange={handleInputChange}
                            />
                            <span
                                className="nrp-terms-link"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && setShowTermsModal(true)}
                            >
                                {t('accept-terms')}
                            </span>
                        </label>
                        {errors.acceptedTerms && <span className="ff-error">{errors.acceptedTerms}</span>}
                    </div>

                </form>

                {/* ── SIGN IN LINK ── */}
                <div className="nrp-actions">
                    <div className="nrp-signin-row">
                        {t('nrp-already-have-account')}{' '}
                        <Link to="/login">{t('nrp-sign-in')}</Link>
                    </div>
                </div>

                {/* ── FOOTER ── */}
                <div className="nrp-footer">
                    {t('nrp-footer-text-prefix')}{' '}
                    <span
                        className="nrp-footer-link nrp-footer-link--clickable"
                        onClick={() => setShowTermsModal(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setShowTermsModal(true)}
                    >
                        {t('nlp-footer-terms')}
                    </span>{' '}{t('nrp-footer-and')}{' '}
                    <span
                        className="nrp-footer-link nrp-footer-link--clickable"
                        onClick={() => setShowTermsModal(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setShowTermsModal(true)}
                    >
                        {t('nlp-footer-privacy')}
                    </span>
                </div>

                <TermsModal open={showTermsModal} onClose={() => setShowTermsModal(false)} />

            </div>
        </div>
        </>
    );
};

export default RegisterPage;
